import { createFileRoute, Link } from "@tanstack/react-router";
import { ClockIcon, LogInIcon } from "lucide-react";
import { Image } from "@/components/image";
import { Button } from "@/components/ui/button";
import { FAQ } from "@/components/www/sections/angebote/faq";
import { Hero } from "@/components/www/sections/angebote/hero";
import { SchoolBooking } from "@/components/www/sections/angebote/school-booking";
import {
  ScaffoldSection,
  ScaffoldSectionContainer,
  ScaffoldSectionDescription,
  ScaffoldSectionHeader,
  ScaffoldSectionTitle,
} from "@/components/www/sections/scaffold";
import { ACTIVITIES__NEW__ } from "@/lib/constants";
import { seo } from "@/lib/seo";

export const Route = createFileRoute(
  "/_www/angebote/3d-schwarzlicht-minigolf/"
)({
  component: RouteComponent,
  head: () =>
    seo({
      title: "3D-Schwarzlicht Minigolf – Alte Papierfabrik Wuppertal",
      description:
        "3D-Schwarzlicht Indoor Minigolf in der Alten Papierfabrik Wuppertal. Vier unglaubliche Welten – Öffnungszeiten, Preise und online buchen.",
      path: "/angebote/3d-schwarzlicht-minigolf",
      image: "/images/og/minigolf.jpg",
      imageAlt:
        "Leuchtende 3D-Schwarzlicht Minigolfbahn mit Astronauten-Wandmalerei",
    }),
});

const hours = [
  {
    days: "Mo - Do",
    open: "12:00 - 22:00",
    lastEntry: "21:00",
  },
  {
    days: "Fr & Sa",
    open: "12:00 - 00:00",
    lastEntry: "23:00",
  },
  {
    days: "So & Feiertage",
    open: "11:00 - 22:00",
    lastEntry: "21:00",
  },
];

const prices = [
  {
    days: "Mo - Do",
    adults: 9.9,
    children: 8.9,
  },
  {
    days: "Fr - So",
    adults: 10.9,
    children: 9.9,
  },
];

const currencyFormatter = new Intl.NumberFormat("de", {
  style: "currency",
  currency: "EUR",
});

function RouteComponent() {
  return (
    <>
      <Hero
        backgroundVideo={{
          src: "/videos/minigolf.mp4",
          alt: "3D Schwarzlicht Minigolf",
          poster: "/images/minigolf_1.png",
        }}
        bookingButton={{
          label: "Jetzt buchen",
          href: "#buchen",
          target: "_self",
          backgroundColor: ACTIVITIES__NEW__.minigolf.brand.primaryColor,
          foregroundColor:
            ACTIVITIES__NEW__.minigolf.brand.primaryForegroundColor,
        }}
        description="3D-Schwarzlicht Indoor Minigolf Anlage"
        highlights={ACTIVITIES__NEW__.minigolf.highlights}
        logo={ACTIVITIES__NEW__.minigolf.brand.logo}
        primaryColor={ACTIVITIES__NEW__.minigolf.brand.primaryColor}
        title="Ein Abenteuer in 3D!"
      />
      <SchoolBooking activity="minigolf" />
      <ScaffoldSection className="py-0 md:gap-4 md:py-4">
        <ScaffoldSectionContainer className="md:grid lg:grid-cols-2">
          <div className="pb-12 md:pb-0">
            <ScaffoldSectionHeader className="mb-6">
              <ScaffoldSectionTitle
                style={{
                  textBoxTrim: "trim-end",
                }}
              >
                Öffnungszeiten
              </ScaffoldSectionTitle>
            </ScaffoldSectionHeader>
            <div
              className="overflow-hidden rounded-2xl shadow-xl"
              style={{
                backgroundColor: ACTIVITIES__NEW__.minigolf.brand.primaryColor,
                color: ACTIVITIES__NEW__.minigolf.brand.primaryForegroundColor,
              }}
            >
              {/* Hours List */}
              <div className="space-y-3 p-3">
                {hours.map((schedule, index) => (
                  <div
                    className="rounded-xl bg-background p-4 text-white transition-all hover:scale-[1.02] sm:p-5"
                    key={index}
                    style={
                      {
                        // backgroundColor: "rgba(0, 0, 0, 0.08)",
                        // color: ACTIVITIES__NEW__.minigolf.brand.primaryColor,
                      }
                    }
                  >
                    <div className="flex flex-col gap-3">
                      {/* Days */}
                      <div className="min-w-[140px] font-medium text-lg leading-trim sm:text-xl">
                        {schedule.days}
                      </div>

                      {/* Times Container */}
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-6 lg:flex-col lg:items-start lg:gap-3">
                        {/* Opening Hours */}
                        <div className="flex items-center gap-2">
                          <ClockIcon className="size-5 opacity-70" />
                          <span className="font-normal text-base leading-trim">
                            {schedule.open}
                          </span>
                        </div>

                        {/* Divider for desktop */}
                        <div className="hidden h-6 w-px bg-current opacity-20 sm:block lg:hidden" />

                        {/* Last Entry */}
                        <div className="ml-1 flex items-center gap-2 text-sm opacity-80 sm:text-base">
                          <LogInIcon className="size-4" />
                          <span className="leading-trim">
                            Letzter Einlass:{" "}
                            <span className="font-medium">
                              {schedule.lastEntry} Uhr
                            </span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Footer Note */}
              <div
                className="border-t px-6 py-4 text-sm sm:px-8"
                style={{
                  backgroundColor: "rgba(0, 0, 0, 0.05)",
                  borderColor: "rgba(0, 0, 0, 0.1)",
                }}
              >
                <div className="flex items-start gap-2">
                  <svg
                    className="mt-0.5 h-4 w-4 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      clipRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      fillRule="evenodd"
                    />
                  </svg>
                  <span className="opacity-75">
                    Bitte beachten Sie, dass der letzte Einlass eine Stunde vor
                    Schließung erfolgt.
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="pb-12 md:pb-0">
            <ScaffoldSectionHeader className="mb-6">
              <ScaffoldSectionTitle>Preise</ScaffoldSectionTitle>
            </ScaffoldSectionHeader>
            <div className="overflow-hidden rounded-2xl bg-neutral-600 shadow-xl">
              {/* Hours List */}
              <div className="space-y-3 p-3">
                {prices.map((pricing, index) => (
                  <div
                    className="rounded-xl p-4 transition-all hover:scale-[1.02] sm:p-5"
                    key={index}
                    style={{
                      backgroundColor: "rgba(0, 0, 0, 0.08)",
                    }}
                  >
                    <div className="flex flex-col gap-3">
                      {/* Days */}
                      <div className="min-w-[140px] font-medium text-lg leading-trim sm:text-xl">
                        {pricing.days}
                      </div>

                      {/* Times Container */}
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-6 lg:flex-col lg:items-start lg:gap-3">
                        {/* Opening Hours */}
                        <div className="flex w-full items-center justify-between gap-2">
                          <span className="text-sm leading-trim">
                            Erwachsene ab 16 Jahren
                          </span>
                          <span className="font-normal text-sm leading-trim">
                            {currencyFormatter.format(pricing.adults)}
                          </span>
                        </div>
                        <div className="flex w-full items-center justify-between gap-2">
                          <span className="text-sm leading-trim">
                            Kinder von 6 bis 15 Jahre
                          </span>
                          <span className="font-normal text-sm leading-trim">
                            {currencyFormatter.format(pricing.children)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Footer Note */}
              <div
                className="border-t px-6 py-4 text-sm sm:px-8"
                style={{
                  backgroundColor: "rgba(0, 0, 0, 0.05)",
                  borderColor: "rgba(0, 0, 0, 0.1)",
                }}
              >
                <div className="flex items-start gap-2">
                  <svg
                    className="mt-0.5 h-4 w-4 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      clipRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      fillRule="evenodd"
                    />
                  </svg>
                  <span className="opacity-75">
                    Mindestalter 6 Jahre / Einlass unter 16 Jahre nur mit
                    erwachsener Begleitperson möglich. Die Begleitperson ist
                    zahlungspflichtig.
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="pb-12 md:pb-0 lg:col-span-2">
            <ScaffoldSectionHeader className="mb-6">
              <ScaffoldSectionTitle>
                Häufig gestellte Fragen
              </ScaffoldSectionTitle>
            </ScaffoldSectionHeader>
            <FAQ
              backgroundColor={ACTIVITIES__NEW__.minigolf.brand.primaryColor}
              foregroundColor={
                ACTIVITIES__NEW__.minigolf.brand.primaryForegroundColor
              }
              questions={[
                {
                  question: "Wie kann ich die Bahnen buchen?",
                  answer: (
                    <div>
                      <p>
                        <strong>Bitte immer online buchen!</strong> Im
                        Buchungskalender sehen Sie sofort, ob Ihr Wunschtermin
                        verfügbar ist.
                      </p>
                      <p>
                        Wenn Sie mehr Spieler als verfügbare Plätze sind,
                        einfach alle weiteren Teilnehmer 15 Minuten später
                        buchen!
                      </p>
                      <p>
                        Nach Ihrer Online-Buchung erhalten Sie von uns eine
                        Buchungsbestätigung per E-Mail, bitte diese bei Ihrem
                        Besuch vorlegen.
                      </p>
                      <p>
                        <em>
                          Die Einlasszeit beträgt max. 15 Min. (es ist NICHT die
                          Spielzeit) je nach Gruppengröße.
                        </em>
                      </p>
                    </div>
                  ),
                },
                {
                  question:
                    "Kann ich meine Reservierung bearbeiten oder stornieren?",
                  answer: (
                    <div>
                      <p className="mb-2">
                        Ja, nach erfolgreicher Reservierung erhalten Sie eine
                        Bestätigungs-E-Mail mit Links zur Bearbeitung und
                        Stornierung Ihrer Reservierung.
                      </p>
                      <p className="mb-2">
                        Über diese Links können Sie Ihre Buchungsdetails
                        jederzeit anpassen oder die Reservierung bei Bedarf
                        stornieren.
                      </p>
                      <p>
                        <strong>Wichtig:</strong> Änderungen oder Stornierungen
                        sind bis zu 8 Stunden vor der Spielzeit möglich. Sollten
                        Sie die Spielzeit nicht in Anspruch nehmen können,
                        bitten wir Sie, diese auf jeden Fall zu stornieren.
                      </p>
                    </div>
                  ),
                },
                {
                  question: "Brauche ich eine 3D-Brille oder spezielle Schuhe?",
                  answer: (
                    <div>
                      <p className="mb-2">
                        Die 3D-Brillen erhalten Sie für Ihre Spieldauer
                        kostenfrei am Counter. Es kann aber auch ohne 3D-Brille
                        gespielt werden.
                      </p>
                      <p>
                        Für die Anlage müssen keine speziellen Schuhe getragen
                        werden.
                      </p>
                    </div>
                  ),
                },
                {
                  question: "Darf ich eigene Getränke und Speisen mitbringen?",
                  answer: (
                    <div>
                      <p className="mb-2">
                        Die Mitnahme von eigenen Getränken und Speisen ist
                        untersagt.
                      </p>
                      <p className="mb-2">
                        Bei uns können Sie Getränke-Flaschen kaufen und diese in
                        die dafür vorgesehenen Kisten bei Bedarf auf der Anlage
                        stellen.
                      </p>
                      <p>
                        <strong>Tipp:</strong> Unser Restaurant "Aposto" direkt
                        im Gebäude der Alten Papierfabrik freut sich auf Ihren
                        Besuch. Täglich ab 17 Uhr geöffnet!
                      </p>
                    </div>
                  ),
                },
                {
                  question: "Gibt es Gruppentarife?",
                  answer: (
                    <div>
                      <p className="mb-2">
                        Gruppen bitte online als normale Besucher buchen, da wir
                        hierfür keine Gruppentarife anbieten.
                      </p>
                      <p className="mb-2">
                        Für eine geschlossene Gesellschaft ab 40 Teilnehmern
                        erstellen wir auf Anfrage ein individuelles Angebot -
                        einfach unser Kontaktformular nutzen!
                      </p>
                      <p className="mb-2">
                        <strong>Wichtig:</strong> Bitte beachten Sie, dass wir
                        Samstag & Sonntag keine Vergünstigungen für Gruppen
                        anbieten können!
                      </p>
                      <p>
                        Gerne können Sie mit Ihrer Gruppe anschließend bei uns
                        essen & trinken. Bitte fragen Sie uns.
                      </p>
                    </div>
                  ),
                },
                {
                  question: "Kann ich einen Kindergeburtstag feiern?",
                  answer: (
                    <div>
                      <p className="mb-2">
                        <strong>Zeiten für Kindergeburtstage:</strong>
                      </p>
                      <ul className="mb-3 list-outside list-disc space-y-1 pl-4">
                        <li>Montag bis Freitag: 14:00 bis 16:00 Uhr</li>
                        <li>Samstag: 10:00 bis 14:00 Uhr</li>
                        <li>Sonntag: 10:00 bis 16:00 Uhr (letzter Einlass)</li>
                      </ul>
                      <p className="mb-2">
                        <strong>Wichtige Regeln:</strong>
                      </p>
                      <ul className="mb-3 list-outside list-disc space-y-1 pl-4">
                        <li>
                          Pro 5 Kinder/Jugendliche ist 1 Erwachsener als
                          Begleitperson nötig
                        </li>
                        <li>Die Begleitperson ist zahlungspflichtig</li>
                        <li>Mindestalter: 6 Jahre</li>
                        <li>
                          Einlass unter 16 Jahre nur mit erwachsener
                          Begleitperson
                        </li>
                        <li>
                          Jugendliche ab 16 Jahre können ohne Begleitung bis
                          24:00 Uhr spielen
                        </li>
                      </ul>
                      <p className="mb-2">
                        <strong>Birthday-Paket:</strong> Bei Voranmeldung
                        (mindestens 2 Tage) können Sie unser
                        Schul/Birthday-Paket buchen: Burger/Schnitzel/Nuggets
                        mit Pommes, Muffin und ein Softgetränk für nur 9,50€
                        (8,50€ ohne Muffin).
                      </p>
                      <p>
                        <strong>
                          Die Reservierung eines Tisches ist nur telefonisch
                          möglich!
                        </strong>
                      </p>
                    </div>
                  ),
                },
                {
                  question: "Können Schulklassen das Angebot nutzen?",
                  answer: (
                    <div>
                      <p className="mb-2">
                        Gerne können wir für Ihre Klasse auch vormittags öffnen.
                      </p>
                      <p className="mb-2">
                        <strong>Wichtige Informationen:</strong>
                      </p>
                      <ul className="mb-3 list-outside list-disc space-y-1 pl-4">
                        <li>
                          Die Klasse wird in Gruppen aufgeteilt (5-7 Spieler pro
                          Gruppe)
                        </li>
                        <li>Spielzeit für die Klasse beträgt ca. 90 Minuten</li>
                        <li>
                          Pro Gruppe sollte eine Aufsichtsperson sein
                          (mindestens 16 Jahre alt)
                        </li>
                        <li>
                          Speisen, Getränke, Kaugummis, Stifte etc. sind im
                          Spielfeld untersagt
                        </li>
                        <li>
                          Alle Spieler & Aufsichtspersonen zahlen 8,90€ Eintritt
                        </li>
                      </ul>
                      <p>
                        <strong>Reservierung:</strong> Per E-Mail unter
                        buchen@golfing-underground.de
                        <br />
                        Bitte immer angeben: Datum, Uhrzeit, Gruppengröße,
                        Anzahl der Aufsichtspersonen.
                      </p>
                    </div>
                  ),
                },
                {
                  question: "Wo kann ich parken?",
                  answer: (
                    <div>
                      <p className="mb-2">
                        Für unsere Gäste stehen Parkplätze auf unserem Parkplatz
                        direkt vor der Alten Papierfabrik zur Verfügung!
                      </p>
                      <p>
                        Sollten diese alle besetzt sein, finden Sie rund um die
                        Alte Papierfabrik genug öffentliche Parkmöglichkeiten.
                      </p>
                    </div>
                  ),
                },
              ]}
            />
          </div>
        </ScaffoldSectionContainer>
      </ScaffoldSection>
      <ScaffoldSection className="relative">
        <Image
          alt="Minigolf Galerie"
          className="absolute inset-0 z-[-1] size-full object-cover"
          height={1000}
          src="/images/minigolf/gallery/1.jpg"
          width={1000}
        />
        <ScaffoldSectionContainer>
          <ScaffoldSectionHeader className="mt-40 gap-6">
            <ScaffoldSectionTitle className="max-w-[30rem] text-left">
              Tauche ein in vier unglaubliche Welten
            </ScaffoldSectionTitle>
            <Button
              asChild
              className="ml-auto w-fit md:ml-0"
              size="xl"
              variant="outline"
            >
              <Link to="/angebote/3d-schwarzlicht-minigolf/galerie">
                Zur Galerie
              </Link>
            </Button>
          </ScaffoldSectionHeader>
        </ScaffoldSectionContainer>
      </ScaffoldSection>
      <ScaffoldSection id="buchen">
        <ScaffoldSectionContainer>
          <ScaffoldSectionHeader>
            <ScaffoldSectionTitle>Jetzt Buchen</ScaffoldSectionTitle>
            <ScaffoldSectionDescription>
              Buchen Sie bequem online über unseren Buchungskalender! Hier sehen
              Sie sofort die Verfügbarkeit für Ihren Wunschtermin. Falls Sie
              mehr Spieler als verfügbare Plätze haben, buchen Sie die
              zusätzlichen Teilnehmer einfach 15 Minuten später ein. Die
              tatsächliche Spieldauer beträgt je nach Gruppengröße etwa 1 bis
              1,5 Stunden.
            </ScaffoldSectionDescription>
          </ScaffoldSectionHeader>
          <iframe
            frameBorder="0"
            height="900"
            scrolling="yes"
            src="https://4bowl.de/altepapierfabrik/online/?widget=1&token=Z1N6RnBqSmk5WmNSb1B3WE5Mc1Rldz09"
            style={{ minWidth: "100%" }}
            title="Minigolf buchen"
            width="100%"
          />
        </ScaffoldSectionContainer>
      </ScaffoldSection>
    </>
  );
}
